import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AdminAccountState, UserStats } from 'Interfaces/Admin';
import { AdminInitResponse } from 'api/adminApi';

const INITIAL_STATE: AdminAccountState = {
  loaded: false,

  responsible_user: false,
  contactEmail: null,
  transcriptionUsage: 0,
  transcriptionUsageUnits: null,
  transcriptionBudget: 0,
  transcriptionBudgetUnits: null,
  aiCredits: 0,
  aiUsage: 0,

  activeUsers: 0,
  officeUsers: 0,
  maxUsers: 0,
  monthlyLicences: 0,
  pricingPlan: null,
  paymentSchedule: null,
  maxPasswordAge: 0,

  groupId: null,
  userId: null,
  hasSuperUserGroup: false,
  superUserGroup: false,
  hasOrganization: false,
  isOrganization: false,
  samlIdpMetadata: false,
  samlEnabled: false,
  accessSharedDataOnlyEnabled: false,
  adminDisablePassword: false,
  logDate: null,
  db: null,
};

/** The session values admin.tmpl.jsp writes onto window. */
export type AdminSession = Partial<Pick<AdminAccountState,
'groupId' | 'userId' | 'hasSuperUserGroup' | 'superUserGroup' | 'hasOrganization' | 'isOrganization' |
'samlIdpMetadata' | 'adminDisablePassword' | 'logDate' | 'maxUsers' | 'monthlyLicences' |
'pricingPlan' | 'paymentSchedule' | 'maxPasswordAge' | 'activeUsers' | 'officeUsers' | 'db'
>>;

const adminAccountSlice = createSlice({
  name: 'adminAccount',
  initialState: INITIAL_STATE,
  reducers: {
    setAdminSession(state, action: PayloadAction<AdminSession>) {
      return { ...state, ...action.payload };
    },
    /**
     *  Everything the admin tool needs about the account. Most of this used to
     *  be written onto window by admin.tmpl.jsp; the keys that come straight
     *  from the settings table keep their snake_case names on the wire.
     */
    loadedAdminInit(state, action: PayloadAction<AdminInitResponse>) {
      const init = action.payload;
      const num = (value: unknown): number => {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
      };
      const bool = (value: unknown): boolean => value === true || value === 'true';

      return {
        ...state,
        loaded: true,

        responsible_user: init.responsible_user === true,
        contactEmail: init.contactEmail ?? null,
        transcriptionUsage: num(init.transcriptionUsage),
        transcriptionUsageUnits: init.transcriptionUsageUnits ?? null,
        transcriptionBudget: num(init.transcriptionBudget),
        transcriptionBudgetUnits: init.transcriptionBudgetUnits ?? null,
        aiCredits: num(init.aiCredits),
        aiUsage: num(init.aiUsage),

        activeUsers: num(init.activeUsers),
        officeUsers: num(init.no_of_office_users),
        maxUsers: num(init.max_users),
        monthlyLicences: num(init.monthlyLicences),
        pricingPlan: init.pricing_plan ?? null,
        paymentSchedule: init.payment_schedule ?? null,
        maxPasswordAge: num(init.max_password_age),

        groupId: num(init.groupId) || state.groupId,
        userId: num(init.userId) || state.userId,
        hasSuperUserGroup: init.hasSuperUserGroup === true,
        superUserGroup: init.superUserGroup === true,
        hasOrganization: init.hasOrganization === true,
        isOrganization: init.isOrganization === true,
        // Present and non-empty means the account has SAML configured.
        samlIdpMetadata: !!init.saml_idp_metadata,
        samlEnabled: init.samlEnabled === true,
        accessSharedDataOnlyEnabled: init.accessSharedDataOnlyEnabled === true,
        adminDisablePassword: bool(init.admin_disable_password),
        db: init.instance ?? state.db,
      };
    },
    loadedUserStats(state, action: PayloadAction<UserStats>) {
      const { activeUsers, noOfOfficeUsers, maxUsers, pricingPlan } = action.payload;
      state.activeUsers = Number(activeUsers) || 0;
      state.officeUsers = Number(noOfOfficeUsers) || 0;
      state.maxUsers = Number(maxUsers) || 0;
      if (pricingPlan) {
        state.pricingPlan = pricingPlan;
      }
    },
    /**
     *  Nudges the seat counters without a round trip, mirroring the optimistic
     *  updates UserSync and DetailsView made to #activeUsers / #officeUsers.
     */
    adjustSeatCounts(state, action: PayloadAction<{ active: number; office: number }>) {
      state.activeUsers = Math.max(0, state.activeUsers + action.payload.active);
      state.officeUsers = Math.max(0, state.officeUsers + action.payload.office);
    },
    setMaxPasswordAge(state, action: PayloadAction<number>) {
      state.maxPasswordAge = action.payload;
    },
    setSuperUserGroupFlags(state, action: PayloadAction<boolean>) {
      state.superUserGroup = action.payload;
      state.hasSuperUserGroup = action.payload;
    },
  },
});

export const {
  setAdminSession, loadedAdminInit, loadedUserStats, adjustSeatCounts,
  setMaxPasswordAge, setSuperUserGroupFlags,
} = adminAccountSlice.actions;

export default adminAccountSlice.reducer;
